import {
  Button,
  FilterItem,
  FlexBox,
  space,
  theme,
} from "@waldner-laboreinrichtungen/component-system";
import { t } from "i18next";
import type { FC } from "react";
import { useEffect, useState } from "react";
import styledComponents from "styled-components";

import { ReactComponent as Battery0BarIcon } from "../../public/material-icons/battery-0-bar.svg";
import { ReactComponent as Battery1BarIcon } from "../../public/material-icons/battery-1-bar.svg";
import { ReactComponent as Battery2BarIcon } from "../../public/material-icons/battery-2-bar.svg";
import { ReactComponent as Battery3BarIcon } from "../../public/material-icons/battery-3-bar.svg";
import { ReactComponent as Battery4BarIcon } from "../../public/material-icons/battery-4-bar.svg";
import { ReactComponent as Battery5BarIcon } from "../../public/material-icons/battery-5-bar.svg";
import { ReactComponent as Battery6BarIcon } from "../../public/material-icons/battery-6-bar.svg";
import { ReactComponent as BatteryFullIcon } from "../../public/material-icons/battery-full.svg";
import { ReactComponent as WarningIcon } from "../../public/material-icons/warning.svg";
import { useChannelValuesForDevice } from "../hooks/use-channels";
import type { Incident, Unit } from "../view-model";
import { ChannelExport } from "./channel-export";
import { UnitFunctions } from "./unit-functions.tsx";

export type DeviceState = "initialization" | "operate" | "sleep" | "shutdown";

// Connectivity status — IDENTICAL to the device header's "Verbunden" indicator
// (device.state.status, from resolveDeviceStatus). The FU dot mirrors the header
// 1:1 so the two can never diverge (online = green, anything else = grey).
export type DeviceStatus = "online" | "offline" | "error" | "initialization";

export type ChannelValuesProps = {
  units: Unit[];
  deviceStatus?: DeviceStatus;
  incidents?: Incident[];
  onRefetch?: () => void;
};

const BATTERY_ICON_SIZE = "24px";

const StyledBatteryIcon = styledComponents.div`
  width: ${BATTERY_ICON_SIZE};
  height: ${BATTERY_ICON_SIZE};
  vertical-align: middle;
  margin: 0 ${space(1)};
  display: inline-flex;
  align-items: center;
  transform: rotate(90deg);
`;

const StyledBatteryFullIcon = styledComponents(BatteryFullIcon)`
  width: ${BATTERY_ICON_SIZE};
  height: ${BATTERY_ICON_SIZE};
`;

const StyledBattery0BarIcon = styledComponents(Battery0BarIcon)`
  width: ${BATTERY_ICON_SIZE};
  height: ${BATTERY_ICON_SIZE};
`;

const StyledBattery1BarIcon = styledComponents(Battery1BarIcon)`
  width: ${BATTERY_ICON_SIZE};
  height: ${BATTERY_ICON_SIZE};
`;

const StyledBattery2BarIcon = styledComponents(Battery2BarIcon)`
  width: ${BATTERY_ICON_SIZE};
  height: ${BATTERY_ICON_SIZE};
`;

const StyledBattery3BarIcon = styledComponents(Battery3BarIcon)`
  width: ${BATTERY_ICON_SIZE};
  height: ${BATTERY_ICON_SIZE};
`;

const StyledBattery4BarIcon = styledComponents(Battery4BarIcon)`
  width: ${BATTERY_ICON_SIZE};
  height: ${BATTERY_ICON_SIZE};
`;

const StyledBattery5BarIcon = styledComponents(Battery5BarIcon)`
  width: ${BATTERY_ICON_SIZE};
  height: ${BATTERY_ICON_SIZE};
`;

const StyledBattery6BarIcon = styledComponents(Battery6BarIcon)`
  width: ${BATTERY_ICON_SIZE};
  height: ${BATTERY_ICON_SIZE};
`;

// Same rule as the device header's connectionColor (device-sidebar): green iff
// online, grey otherwise — so the FU dot and the header are always consistent.
const StatusCircle = styledComponents.div<{ status: DeviceStatus }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ status }) =>
    status === "online"
      ? theme.colorPalette.green500
      : theme.colorPalette.grey300};
  margin-right: ${space(2)};
`;

const getBatteryIcon = (batteryValue: number) => {
  if (batteryValue === 100) {
    return <StyledBatteryFullIcon />;
  }

  const batteryLevel = Math.ceil((batteryValue / 100) * 6);

  switch (batteryLevel) {
    case 0: {
      return <StyledBattery0BarIcon />;
    }
    case 1: {
      return <StyledBattery1BarIcon />;
    }
    case 2: {
      return <StyledBattery2BarIcon />;
    }
    case 3: {
      return <StyledBattery3BarIcon />;
    }
    case 4: {
      return <StyledBattery4BarIcon />;
    }
    case 5: {
      return <StyledBattery5BarIcon />;
    }
    case 6: {
      return <StyledBattery6BarIcon />;
    }
    default: {
      return <StyledBattery0BarIcon />;
    }
  }
};
// FIXME: extract TabItem to own component!
const getDeviceFilterName = (
  unit: Unit,
  deviceStatus: DeviceStatus,
  batteryValue: number | undefined,
  incidentSeverity: "critical" | "warning" | null
) => (
  <FlexBox alignItems="center" gap={space(1)}>
    {/* Bei aktivem Alarm ERSETZT das Warning-Icon den Online-Punkt — nicht
        beide nebeneinander. Ohne Alarm: der normale Online/Offline-Status. */}
    {incidentSeverity ? (
      <WarningIcon
        color={
          incidentSeverity === "critical"
            ? theme.color.error
            : theme.color.warning
        }
        fontSize={theme.font.size.default.lg}
      />
    ) : (
      <StatusCircle status={deviceStatus} title={deviceStatus} />
    )}
    {unit.name}
    {batteryValue !== undefined && batteryValue > 0 && (
      <StyledBatteryIcon>{getBatteryIcon(batteryValue)}</StyledBatteryIcon>
    )}
  </FlexBox>
);

type DeviceFilterItemProps = {
  unit: Unit;
  isSelected: boolean;
  onSelect: (unit: Unit) => void;
  deviceStatus: DeviceStatus;
  incidents?: Incident[];
};

const DeviceFilterItem: FC<DeviceFilterItemProps> = ({
  deviceStatus,
  incidents,
  isSelected,
  onSelect,
  unit,
}) => {
  const { channels } = useChannelValuesForDevice(
    unit.id,
    unit.functions,
    unit.deviceId
  );
  const batteryValue = channels?.battery?.current?.value as number | undefined;

  // Key incidents by browse path — `name`/`unit.name` are display labels and
  // may diverge (DisplayName vs BrowseName). FU segment is path index 3 in
  // both the canonical and the alias-mounted variant.
  // critical beats warning so the worst level drives the icon color.
  const unitFu = unit.browsePath.split("/")[3];
  const unitIncidents = (incidents ?? []).filter(
    (i) => i.browsePath?.split("/")[3] === unitFu
  );
  let incidentSeverity: "critical" | "warning" | null = null;
  if (unitIncidents.some((i) => i.severity === "critical")) {
    incidentSeverity = "critical";
  } else if (unitIncidents.some((i) => i.severity === "warning")) {
    incidentSeverity = "warning";
  }

  return (
    <FilterItem
      defaultChecked={isSelected}
      id={unit.id}
      key={unit.id}
      name="selectDevice"
      onChange={() => onSelect(unit)}
      value={unit.id}
    >
      {getDeviceFilterName(unit, deviceStatus, batteryValue, incidentSeverity)}
    </FilterItem>
  );
};

export const DeviceFunctions: FC<ChannelValuesProps> = ({
  deviceStatus = "offline",
  incidents,
  onRefetch,
  units,
}) => {
  const [selectedUnit, setSelectedUnit] = useState(units[0]);
  const [isOpen, setIsOpen] = useState(false);

  // Subscribe to real-time channel updates for the selected unit
  const { channels: liveChannels } = useChannelValuesForDevice(
    selectedUnit?.id || "",
    selectedUnit?.functions || {},
    selectedUnit?.deviceId
  );

  // Sync selectedUnit when units array updates (e.g., from WebSocket)
  // Also select first unit if nothing is selected yet (initial load with empty array)
  const selectedUnitId = selectedUnit?.id;
  useEffect(() => {
    if (selectedUnitId) {
      const currentUnit = units.find((unit) => unit.id === selectedUnitId);
      if (currentUnit) {
        setSelectedUnit(currentUnit);
      }
    } else if (units.length > 0) {
      setSelectedUnit(units[0]);
    }
  }, [units, selectedUnitId]);

  // Use live channels if available, otherwise fall back to unit.functions
  // Filter out disabled channels (isEnabled === false)
  const getDisplayChannels = () => {
    const channelMap = liveChannels || selectedUnit?.functions;
    const channels = channelMap ? Object.values(channelMap) : [];
    return channels.filter((ch) => ch.isEnabled !== false);
  };
  const displayChannels = getDisplayChannels();

  return (
    <>
      <FlexBox
        alignItems="center"
        gap={space(2)}
        justifyContent="space-between"
        marginBottom={space(1)}
      >
        <FlexBox alignItems="center" gap={space(2)}>
          {units.map((unit) => (
            <DeviceFilterItem
              deviceStatus={deviceStatus}
              incidents={incidents}
              isSelected={unit.id === selectedUnit?.id}
              key={unit.id}
              onSelect={setSelectedUnit}
              unit={unit}
            />
          ))}
        </FlexBox>
        <Button onClick={() => setIsOpen(true)}>
          {t("export.dataexport")}
        </Button>
      </FlexBox>
      <ChannelExport
        channels={selectedUnit ? Object.values(selectedUnit.functions) : []}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />
      {
        /* selectedUnit is empty on initial rendering! */
        selectedUnit && (
          <UnitFunctions
            channels={displayChannels}
            incidents={incidents}
            onRefetch={onRefetch}
            unit={selectedUnit}
          />
        )
      }
    </>
  );
};
